import axios from 'axios';
import {
  AddCollectionContentRequest,
  ContentCollection,
  CreateContentCollectionRequest,
  CreateContentCollectionResponse,
  GetContentCollectionResponse,
  Page,
  RemoveCollectionContentRequest,
  UpdateContentCollectionRequest,
  UpdateContentCollectionResponse,
} from '@ilteducation/content-collections-types';
import qs from 'qs';
import store from '@/store';

const getHeaders = () => ({
  'Content-Type': 'application/json',
});

const convertAndFilterObject = (filter?: Record<string, any>): string[] => {
  if (filter === undefined) {
    return [];
  }

  return Object.entries(filter)
    .filter(([, value]) => value !== undefined && value !== '' && value !== null)
    .map(([key, value]) => `${key}:${value}`);
};

const getApiUrl = (realm: string): string =>
  realm === 'us' ? process.env.VUE_APP_COLLECTIONS_US_API_URL! : process.env.VUE_APP_COLLECTIONS_API_URL!;

const setLoading = (isLoading: boolean): void => {
  store.commit('api/setLoading', isLoading);
};

export const getList = async (
  filter?: Record<string, any>,
  cursor?: string | null,
  limit?: 25,
): Promise<Page<ContentCollection>> => {
  const urlQs = qs.stringify(
    { type: 'curated', filter: convertAndFilterObject(filter), cursor, limit },
    { arrayFormat: 'repeat' },
  );
  const params = urlQs ? `?${urlQs}` : '';

  setLoading(true);
  try {
    const response = await axios.get(`${getApiUrl(filter?.realm ?? '')}${params}`, {
      headers: getHeaders(),
    });
    return response.data;
  } finally {
    setLoading(false);
  }
};

export const getOne = (realm: string, collectionId: string): Promise<ContentCollection | null> =>
  axios
    .get(`${getApiUrl(realm)}${collectionId}`)
    .then((response) => response.data as GetContentCollectionResponse)
    .catch((error) => {
      if (error.response.status === 404) {
        return Promise.resolve(null);
      }
      return Promise.reject(error);
    });

export const create = (realm: string, payload: CreateContentCollectionRequest): Promise<ContentCollection> =>
  axios
    .post(`${getApiUrl(realm)}`, payload, {
      headers: getHeaders(),
    })
    .then((response) => response.data as CreateContentCollectionResponse);

export const destroy = (realm: string, collectionId: string): Promise<void> =>
  axios.delete(`${getApiUrl(realm)}${collectionId}`);

export const update = (
  realm: string,
  collectionId: string,
  payload: UpdateContentCollectionRequest,
): Promise<ContentCollection> =>
  axios
    .patch(`${getApiUrl(realm)}/${collectionId}`, payload, {
      headers: getHeaders(),
    })
    .then((response) => response.data as UpdateContentCollectionResponse);

export const addContent = (realm: string, collectionId: string, payload: AddCollectionContentRequest): Promise<void> =>
  axios.post(`${getApiUrl(realm)}/${collectionId}/contents`, payload).then((response) => response.data);

export const removeContent = (
  realm: string,
  collectionId: string,
  payload: RemoveCollectionContentRequest,
): Promise<void> =>
  axios.post(`${getApiUrl(realm)}/${collectionId}/contents/remove`, payload).then((response) => response.data);
